import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useLocalization } from '@ais-ui/locale-management';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ais-ui/ui-core';

interface SettingsPageProviderProps {
  children: ReactNode;
}

interface SettingsPageContextType {
  unsavedSettings: boolean;
  setUnsavedSettings: React.Dispatch<React.SetStateAction<boolean>>;
  checkNavigationSafety: (onConfirm: () => void) => void;
}

const SettingsPageContext = createContext<SettingsPageContextType | null>(null);

export const SettingsPageProvider = ({
  children,
}: SettingsPageProviderProps) => {
  const { messages } = useLocalization();
  const [unsavedSettings, setUnsavedSettings] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (unsavedSettings) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedSettings]);

  const checkNavigationSafety = useCallback(
    (onConfirm: () => void) => {
      if (unsavedSettings) {
        setShowConfirmDialog(true);
        setPendingNavigation(() => onConfirm);
      } else {
        onConfirm();
      }
    },
    [unsavedSettings]
  );

  const handleConfirm = () => {
    setShowConfirmDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
      setUnsavedSettings(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setPendingNavigation(null);
  };

  return (
    <SettingsPageContext.Provider
      value={{ unsavedSettings, setUnsavedSettings, checkNavigationSafety }}
    >
      {children}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved settings</AlertDialogTitle>
            <AlertDialogDescription>
              You've got unsaved settings, are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageContext.Provider>
  );
};

export const useSettingsPage = () => {
  const context = useContext(SettingsPageContext);
  if (!context) {
    throw new Error(
      'useSettingsPageContext must be used within a SettingsPageContextProvider'
    );
  }
  return context;
};
